/**
 * GallerySection - Station gallery management with drag-to-reorder
 * Up to 10 images, 5MB each, with bulk upload support
 */

import React, { useState, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Stack,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  alpha,
  useTheme,
} from '@mui/material';
import {
  DragDropContext,
  Draggable,
  DropResult,
  DroppableProvided,
  DraggableProvided,
  DraggableStateSnapshot,
  DroppableStateSnapshot,
} from 'react-beautiful-dnd';
import StrictModeDroppable from './StrictModeDroppable';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { GallerySectionProps, IMAGE_TYPE_CONFIGS, CropModalState, INITIAL_CROP_STATE } from './types';
import ImageUploadDropzone from './ImageUploadDropzone';
import ImageCard from './ImageCard';
import ImagePreviewModal from './ImagePreviewModal';
import ImageCropModal from './ImageCropModal';
import { compressImageOnly } from './utils/cropImage';

const GallerySection: React.FC<GallerySectionProps> = ({
  stationId,
  images,
  uploadingImages,
  onUpload,
  onDelete,
  onReorder,
  maxCount,
  disabled = false,
  readOnly = false,
}) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [deleteImageId, setDeleteImageId] = useState<string | null>(null);
  const [cropModal, setCropModal] = useState<CropModalState>(INITIAL_CROP_STATE);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  const config = IMAGE_TYPE_CONFIGS.gallery;
  const currentCount = images.length + uploadingImages.length;
  const remainingSlots = maxCount - currentCount;
  const isFull = remainingSlots <= 0;

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination || disabled || readOnly) return;

      const sourceIndex = result.source.index;
      const destIndex = result.destination.index;

      if (sourceIndex === destIndex) return;

      // Create new order
      const newImages = Array.from(images);
      const [movedImage] = newImages.splice(sourceIndex, 1);
      newImages.splice(destIndex, 0, movedImage);

      // Call reorder with new order of IDs
      const newOrder = newImages.map((img) => img.id);
      onReorder(newOrder);
    },
    [images, onReorder, disabled, readOnly]
  );

  const handleFileSelect = async (files: File[]) => {
    if (files.length === 0 || disabled || readOnly) return;

    // Limit to remaining slots
    const filesToUpload = files.slice(0, remainingSlots);
    if (filesToUpload.length === 0) return;

    if (filesToUpload.length === 1) {
      // Single file: show crop modal with skip option
      const file = filesToUpload[0];
      const previewUrl = URL.createObjectURL(file);
      setCropModal({
        open: true,
        file,
        previewUrl,
      });
    } else {
      // Multiple files: auto-compress all without cropping
      // (cropping each one would be tedious for bulk uploads)
      const compressedFiles = await Promise.all(
        filesToUpload.map((file) => compressImageOnly(file))
      );
      await onUpload(compressedFiles);
    }
  };

  const handleCropComplete = async (processedFile: File) => {
    // Clean up the object URL
    if (cropModal.previewUrl) {
      URL.revokeObjectURL(cropModal.previewUrl);
    }
    setCropModal(INITIAL_CROP_STATE);

    // Check if there are more pending files
    if (pendingFiles.length > 0) {
      // Process next file
      const [nextFile, ...remaining] = pendingFiles;
      setPendingFiles(remaining);
      const previewUrl = URL.createObjectURL(nextFile);
      setCropModal({
        open: true,
        file: nextFile,
        previewUrl,
      });
      // Upload the current processed file
      await onUpload([processedFile]);
    } else {
      // No more files, just upload
      await onUpload([processedFile]);
    }
  };

  const handleCropCancel = () => {
    // Clean up the object URL
    if (cropModal.previewUrl) {
      URL.revokeObjectURL(cropModal.previewUrl);
    }
    setCropModal(INITIAL_CROP_STATE);
    // Clear any pending files
    setPendingFiles([]);
  };

  const handleAddClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      handleFileSelect(Array.from(files));
    }
    event.target.value = '';
  };

  const handleDeleteClick = (imageId: string) => {
    setDeleteImageId(imageId);
  };

  const handleDeleteConfirm = async () => {
    if (deleteImageId) {
      await onDelete(deleteImageId);
    }
    setDeleteImageId(null);
  };

  const handlePreview = (index: number) => {
    setPreviewIndex(index);
  };

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5}>
            <Typography variant="subtitle1" fontWeight={600}>
              {config.label}
            </Typography>
            <Chip
              label={`${currentCount}/${maxCount}`}
              size="small"
              color={isFull ? 'warning' : 'default'}
              sx={{ fontWeight: 500 }}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {readOnly
              ? 'Photo gallery for the mobile app'
              : `${config.description} Drag to reorder.`}
          </Typography>
        </Box>

        {/* Add Button */}
        {!readOnly && !isFull && (
          <Button
            variant="outlined"
            startIcon={<AddPhotoAlternateIcon />}
            onClick={handleAddClick}
            disabled={disabled}
          >
            Add Images
          </Button>
        )}
      </Stack>

      {/* Full Gallery Warning */}
      {isFull && !readOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Gallery is full. Delete images to add more.
        </Alert>
      )}

      {/* Rejected Gallery Images Alert */}
      {images.filter((img) => img.status === 'rejected').length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            {images.filter((img) => img.status === 'rejected').length} gallery image
            {images.filter((img) => img.status === 'rejected').length > 1 ? 's were' : ' was'}{' '}
            rejected
          </Typography>
          {images
            .filter((img) => img.status === 'rejected')
            .map((img) => (
              <Typography key={img.id} variant="body2">
                &bull; {img.rejection_reason || 'No reason provided'}
                {img.rejected_by ? ` (by ${img.rejected_by})` : ''}
              </Typography>
            ))}
        </Alert>
      )}

      {/* Gallery Grid */}
      {images.length > 0 || uploadingImages.length > 0 ? (
        <DragDropContext onDragEnd={handleDragEnd}>
          <StrictModeDroppable droppableId="gallery" direction="horizontal">
            {(provided: DroppableProvided, snapshot: DroppableStateSnapshot) => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  minHeight: 140,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: snapshot.isDraggingOver
                    ? alpha(theme.palette.primary.main, 0.08)
                    : alpha(theme.palette.grey[500], 0.04),
                  border: `2px dashed ${
                    snapshot.isDraggingOver
                      ? theme.palette.primary.main
                      : theme.palette.divider
                  }`,
                  transition: 'all 0.2s',
                }}
              >
                {/* Existing Images (Draggable) */}
                {images.map((image, index) => (
                  <Draggable
                    key={image.id}
                    draggableId={image.id}
                    index={index}
                    isDragDisabled={disabled || readOnly}
                  >
                    {(
                      provided: DraggableProvided,
                      snapshot: DraggableStateSnapshot
                    ) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        sx={{
                          opacity: snapshot.isDragging ? 0.9 : 1,
                          transform: snapshot.isDragging
                            ? 'scale(1.02)'
                            : 'scale(1)',
                          transition: snapshot.isDragging
                            ? 'none'
                            : 'transform 0.2s',
                        }}
                      >
                        <ImageCard
                          image={image}
                          aspectRatio={4 / 3}
                          showDragHandle={!readOnly && !disabled}
                          onPreview={() => handlePreview(index)}
                          onDelete={
                            !readOnly ? () => handleDeleteClick(image.id) : undefined
                          }
                          disabled={disabled}
                        />
                      </Box>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}

                {/* Uploading Images */}
                {uploadingImages.map((uploading) => (
                  <ImageCard
                    key={uploading.id}
                    uploadingImage={uploading}
                    aspectRatio={4 / 3}
                  />
                ))}

                {/* Inline Add Zone (when there's space and not read-only) */}
                {!readOnly && remainingSlots > 0 && images.length > 0 && (
                  <Box
                    onClick={handleAddClick}
                    sx={{
                      width: 160,
                      height: 120,
                      borderRadius: 2,
                      border: `2px dashed ${theme.palette.divider}`,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      opacity: disabled ? 0.5 : 1,
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: disabled
                          ? theme.palette.divider
                          : theme.palette.primary.main,
                        bgcolor: disabled
                          ? 'transparent'
                          : alpha(theme.palette.primary.main, 0.04),
                      },
                    }}
                  >
                    <AddPhotoAlternateIcon
                      sx={{ color: 'text.secondary', fontSize: 32, mb: 0.5 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Add more
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </StrictModeDroppable>
        </DragDropContext>
      ) : (
        // Empty state
        <Box>
          {!readOnly ? (
            <ImageUploadDropzone
              onDrop={handleFileSelect}
              accept={config.acceptTypes}
              maxSize={config.maxSize}
              maxFiles={remainingSlots}
              disabled={disabled}
              label={`PNG, JPEG or WebP - Max ${config.maxSize / (1024 * 1024)}MB per image`}
            />
          ) : (
            <Box
              sx={{
                p: 4,
                borderRadius: 2,
                border: `2px dashed ${theme.palette.divider}`,
                textAlign: 'center',
              }}
            >
              <AddPhotoAlternateIcon
                sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }}
              />
              <Typography color="text.secondary">No gallery images</Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Hidden file input for bulk upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept={config.acceptExtensions}
        multiple
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
      />

      {/* Preview Modal */}
      <ImagePreviewModal
        open={previewIndex !== null}
        images={images}
        initialIndex={previewIndex || 0}
        onClose={() => setPreviewIndex(null)}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteImageId !== null}
        onClose={() => setDeleteImageId(null)}
      >
        <DialogTitle>Delete Image?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this gallery image? This action cannot
            be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteImageId(null)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Crop Modal (optional for gallery) */}
      {cropModal.open && cropModal.file && cropModal.previewUrl && (
        <ImageCropModal
          open={cropModal.open}
          imageSrc={cropModal.previewUrl}
          imageType="gallery"
          originalFile={cropModal.file}
          onComplete={handleCropComplete}
          onCancel={handleCropCancel}
          allowSkip={true}
        />
      )}
    </Box>
  );
};

export default GallerySection;
